import { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  Drawer,
  Group,
  ScrollArea,
  Select,
  Stack,
  Textarea,
  TextInput,
  Title,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { notifications } from '@mantine/notifications';
import { titleRunInputSchema, type TitleRunInputSchema } from '../../lib/schemas';
import { useCreateTitleRun, useUpdateTitleRun } from '../../queries/title-runs';
import { ApiError } from '../../lib/api-error';
import type { TitleRun } from '../../types/api';

interface Props {
  opened: boolean;
  onClose: () => void;
  tractId?: string;
  run?: TitleRun | null;
  onSaved?: (r: TitleRun) => void;
}

const STATUSES = [
  { value: 'open', label: 'Open' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'under_review', label: 'Under review' },
  { value: 'complete', label: 'Complete' },
];

function emptyFor(tractId?: string): TitleRunInputSchema {
  return {
    tract_id: tractId ?? '',
    title: '',
    sovereignty_date: null,
    through_date: null,
    status: 'open',
    remarks: null,
  };
}

export function TitleRunFormDrawer({ opened, onClose, tractId, run, onSaved }: Props) {
  const isEdit = !!run;
  const createM = useCreateTitleRun();
  const updateM = useUpdateTitleRun(run?.id ?? '');
  const form = useForm<TitleRunInputSchema>({
    resolver: zodResolver(titleRunInputSchema),
    defaultValues: emptyFor(tractId),
  });

  useEffect(() => {
    if (opened) {
      form.reset(
        run
          ? {
              tract_id: run.tract_id,
              title: run.title,
              sovereignty_date: run.sovereignty_date,
              through_date: run.through_date,
              status: run.status,
              remarks: run.remarks,
            }
          : emptyFor(tractId),
      );
    }
  }, [opened, run, tractId, form]);

  async function onSubmit(values: TitleRunInputSchema) {
    try {
      const payload = { ...values };
      if (payload.remarks === '') payload.remarks = null;
      const res = isEdit
        ? await updateM.mutateAsync({
            title: payload.title,
            sovereignty_date: payload.sovereignty_date,
            through_date: payload.through_date,
            status: payload.status,
            remarks: payload.remarks,
          })
        : await createM.mutateAsync(payload);
      notifications.show({
        title: isEdit ? 'Run updated' : 'Run created',
        message: res.title_run.title,
        color: 'pine',
      });
      onSaved?.(res.title_run);
      onClose();
    } catch (err) {
      notifications.show({
        title: 'Save failed',
        message: err instanceof ApiError ? err.message : 'Unexpected error',
        color: 'red',
      });
    }
  }

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size="md"
      padding="md"
      title={<Title order={4}>{isEdit ? 'Edit title run' : 'New title run'}</Title>}
      scrollAreaComponent={ScrollArea.Autosize}
    >
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <Stack gap="sm">
          <TextInput
            label="Title"
            required
            placeholder="Base run to sovereignty"
            {...form.register('title')}
            error={form.formState.errors.title?.message}
          />

          <Controller
            control={form.control}
            name="sovereignty_date"
            render={({ field }) => (
              <DateInput
                label="Sovereignty date"
                placeholder="YYYY-MM-DD"
                value={field.value ?? null}
                onChange={(val) => field.onChange(val ?? null)}
                clearable
                valueFormat="YYYY-MM-DD"
              />
            )}
          />

          <Controller
            control={form.control}
            name="through_date"
            render={({ field }) => (
              <DateInput
                label="Through date"
                placeholder="YYYY-MM-DD"
                value={field.value ?? null}
                onChange={(val) => field.onChange(val ?? null)}
                clearable
                valueFormat="YYYY-MM-DD"
              />
            )}
          />

          <Controller
            control={form.control}
            name="status"
            render={({ field }) => (
              <Select
                label="Status"
                data={STATUSES}
                value={field.value ?? 'open'}
                onChange={(v) => field.onChange((v ?? 'open') as TitleRunInputSchema['status'])}
              />
            )}
          />

          <Textarea label="Remarks" autosize minRows={2} {...form.register('remarks')} />

          <Group justify="flex-end" mt="sm">
            <Button variant="default" onClick={onClose} type="button">
              Cancel
            </Button>
            <Button type="submit" loading={form.formState.isSubmitting}>
              {isEdit ? 'Save changes' : 'Create run'}
            </Button>
          </Group>
        </Stack>
      </form>
    </Drawer>
  );
}
